import React, { useEffect, useState } from 'react'
import {
  Button, Form, Input, InputNumber, Modal, Space, Spin, Switch, Upload, Popconfirm, message,
} from 'antd'
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '~/Store'
import {
  getCarouselItemSelected,
  getIsConfirmLoading,
  getIsImageSaving,
  getModalVisibility,
  LoginBrandingActions,
  ModalName,
} from '~/Store/LoginBranding'
import { resolveIdentityAssetUrl } from '~/Services'

// Cùng giới hạn raster ảnh với logo (khớp backend RasterImageValidator dùng chung cho cả 2 tính năng).
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp']
const MAX_IMAGE_SIZE_BYTES = 2 * 1024 * 1024

interface FormValues {
  title: string
  subtitle?: string
  linkText?: string
  linkUrl?: string
  displayOrder: number
  isActive: boolean
}

// Root-relative an toàn (không phải "//host" hay "/\...") hoặc absolute https — khớp
// LoginCarouselLinkValidator phía backend, chỉ để feedback sớm, backend vẫn là nguồn xác thực cuối cùng.
const isSafeLinkUrl = (url: string) => {
  if (url.startsWith('/') && !url.startsWith('//') && !url.startsWith('/\\')) {
    return true
  }
  try {
    return new URL(url).protocol === 'https:'
  } catch {
    return false
  }
}

export const LoginCarouselItemFormModal: React.FC<{ applicationId: string; nextDisplayOrder: number }> = ({
  applicationId,
  nextDisplayOrder,
}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const visible = useAppSelector(getModalVisibility(ModalName.CarouselItem))
  const isConfirmLoading = useAppSelector(getIsConfirmLoading)
  const isImageSaving = useAppSelector(getIsImageSaving)
  const item = useAppSelector(getCarouselItemSelected)
  const [form] = Form.useForm<FormValues>()

  const isEdit = !!item

  // Slide chưa tồn tại (đang "Add") thì chưa có itemId để gọi API upload ngay lúc chọn file — giữ tạm ở
  // đây, chỉ preview bằng object URL, thật sự upload sau khi Save tạo xong slide (xem onSubmit + epic
  // createCarouselItemEpic nối tiếp upload bằng itemId vừa nhận được).
  const [pendingImageFile, setPendingImageFile] = useState<File | undefined>(undefined)
  const [pendingImagePreviewUrl, setPendingImagePreviewUrl] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (item) {
      form.setFieldsValue({
        title: item.title,
        subtitle: item.subtitle || undefined,
        linkText: item.linkText || undefined,
        linkUrl: item.linkUrl || undefined,
        displayOrder: item.displayOrder,
        isActive: item.isActive,
      })
    } else {
      form.resetFields()
      form.setFieldsValue({ displayOrder: nextDisplayOrder, isActive: true })
    }
    setPendingImageFile(undefined)
    setPendingImagePreviewUrl(undefined)
  }, [item])

  // Object URL chỉ sống trong bộ nhớ trình duyệt của tab này — phải tự giải phóng khi đổi ảnh khác
  // hoặc unmount, nếu không sẽ rò rỉ bộ nhớ (trình duyệt không tự thu hồi object URL).
  useEffect(() => () => {
    if (pendingImagePreviewUrl) {
      URL.revokeObjectURL(pendingImagePreviewUrl)
    }
  }, [pendingImagePreviewUrl])

  const close = () => {
    dispatch(LoginBrandingActions.setItemSelected(undefined))
    dispatch(LoginBrandingActions.setModalVisibility({ name: ModalName.CarouselItem, visible: false }))
  }

  const isValidImageFile = (file: File) => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      message.error(t('admin.invalidLogoType'))
      return false
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      message.error(t('admin.logoTooLarge'))
      return false
    }
    return true
  }

  // Item đã tồn tại (đang Edit) -> upload ngay lập tức, có itemId thật rồi.
  const beforeUploadImage = (file: File) => {
    if (!item || !isValidImageFile(file)) {
      return Upload.LIST_IGNORE
    }
    dispatch(LoginBrandingActions.uploadCarouselItemImage({ applicationId, itemId: item.id, file }))
    return false
  }

  // Item chưa tồn tại (đang Add) -> chỉ giữ file + preview, chờ Save xong mới thật sự upload.
  const beforeSelectPendingImage = (file: File) => {
    if (!isValidImageFile(file)) {
      return Upload.LIST_IGNORE
    }
    if (pendingImagePreviewUrl) {
      URL.revokeObjectURL(pendingImagePreviewUrl)
    }
    setPendingImageFile(file)
    setPendingImagePreviewUrl(URL.createObjectURL(file))
    return false
  }

  const onRemovePendingImage = () => {
    if (pendingImagePreviewUrl) {
      URL.revokeObjectURL(pendingImagePreviewUrl)
    }
    setPendingImageFile(undefined)
    setPendingImagePreviewUrl(undefined)
  }

  const onRemoveImage = () => {
    if (item) {
      dispatch(LoginBrandingActions.removeCarouselItemImage({ applicationId, itemId: item.id }))
    }
  }

  const onSubmit = () => {
    form.validateFields().then(values => {
      const linkUrl = values.linkUrl?.trim() || undefined
      const linkText = values.linkText?.trim() || undefined
      const data = {
        title: values.title.trim(),
        subtitle: values.subtitle?.trim() || undefined,
        // Không có LinkUrl -> không gửi LinkText, khớp quy tắc "không có link thì không có CTA" ở backend.
        linkText: linkUrl ? linkText : undefined,
        linkUrl,
        displayOrder: values.displayOrder,
        isActive: values.isActive,
      }

      if (isEdit && item) {
        dispatch(LoginBrandingActions.updateCarouselItem({ applicationId, itemId: item.id, data }))
      } else {
        dispatch(LoginBrandingActions.createCarouselItem({ applicationId, data, pendingImageFile }))
      }
    })
  }

  return (
    <Modal
      visible={visible}
      title={isEdit ? t('admin.editCarouselItem') : t('admin.addCarouselItem')}
      confirmLoading={isConfirmLoading}
      onCancel={close}
      onOk={onSubmit}
      okText={t('save')}
      cancelText={t('cancel')}
      width={560}
    >
      <Spin spinning={isConfirmLoading}>
        <Form form={form} layout="vertical" autoComplete="off">
          <Form.Item
            label={t('admin.carouselTitle')}
            name="title"
            rules={[
              { required: true, whitespace: true, message: t('fieldRequired') },
              { max: 200, message: t('admin.carouselTitleTooLong') },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('admin.carouselSubtitle')}
            name="subtitle"
            rules={[{ max: 500, message: t('admin.carouselSubtitleTooLong') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('admin.carouselLinkUrl')}
            name="linkUrl"
            extra={t('admin.carouselLinkUrlHint')}
            dependencies={['linkText']}
            rules={[
              { max: 2000, message: t('admin.carouselLinkUrlTooLong') },
              {
                validator: async (_, value: string | undefined) => {
                  if (!value) return
                  if (!isSafeLinkUrl(value.trim())) {
                    throw new Error(t('admin.carouselLinkUrlUnsafe'))
                  }
                },
              },
            ]}
          >
            <Input placeholder="/features hoặc https://hicas.vn/..." />
          </Form.Item>

          <Form.Item
            label={t('admin.carouselLinkText')}
            name="linkText"
            dependencies={['linkUrl']}
            rules={[
              { max: 100, message: t('admin.carouselLinkTextTooLong') },
              {
                validator: async (_, value: string | undefined) => {
                  const linkUrl = form.getFieldValue('linkUrl')
                  if (linkUrl && !value?.trim()) {
                    throw new Error(t('admin.carouselLinkTextRequiredWithUrl'))
                  }
                },
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Space size="large">
            <Form.Item
              label={t('admin.carouselDisplayOrder')}
              name="displayOrder"
              rules={[{ required: true, message: t('fieldRequired') }]}
            >
              <InputNumber min={0} />
            </Form.Item>

            <Form.Item label={t('admin.carouselActive')} name="isActive" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>

          <Form.Item label={t('admin.carouselBackgroundImage')}>
            {isEdit && item ? (
              <Space align="center">
                {item.backgroundImageUrl ? (
                  <img
                    src={resolveIdentityAssetUrl(item.backgroundImageUrl)}
                    alt={item.title}
                    style={{
                      width: 120, height: 60, objectFit: 'cover', border: '1px solid #f0f0f0', borderRadius: 4,
                    }}
                  />
                ) : (
                  <span style={{ color: '#999' }}>{t('admin.carouselNoBackgroundImage')}</span>
                )}
                <Upload accept=".png,.jpg,.jpeg,.webp" showUploadList={false} beforeUpload={beforeUploadImage}>
                  <Button icon={<UploadOutlined />} loading={isImageSaving}>
                    {item.backgroundImageUrl ? t('admin.changeLogo') : t('admin.uploadLogo')}
                  </Button>
                </Upload>
                {item.backgroundImageUrl && (
                  <Popconfirm title={t('admin.confirmRemoveCarouselImage')} onConfirm={onRemoveImage}>
                    <Button danger icon={<DeleteOutlined />} loading={isImageSaving}>
                      {t('admin.removeLogo')}
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            ) : (
              <Space align="center">
                {pendingImagePreviewUrl ? (
                  <img
                    src={pendingImagePreviewUrl}
                    alt={pendingImageFile?.name}
                    style={{
                      width: 120, height: 60, objectFit: 'cover', border: '1px solid #f0f0f0', borderRadius: 4,
                    }}
                  />
                ) : (
                  <span style={{ color: '#999' }}>{t('admin.carouselNoBackgroundImage')}</span>
                )}
                <Upload accept=".png,.jpg,.jpeg,.webp" showUploadList={false} beforeUpload={beforeSelectPendingImage}>
                  <Button icon={<UploadOutlined />}>
                    {pendingImageFile ? t('admin.changeLogo') : t('admin.uploadLogo')}
                  </Button>
                </Upload>
                {pendingImageFile && (
                  <Button danger icon={<DeleteOutlined />} onClick={onRemovePendingImage}>
                    {t('admin.removeLogo')}
                  </Button>
                )}
              </Space>
            )}
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  )
}
